import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner, Image } from "react-bootstrap";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";

const decodeHtmlEntities = (html) => {
    if (!html) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
};

export default function ProductEditModal({ show, handleClose, product, onUpdateSuccess, filterData }) {
    const { t } = useContext(TranslatorContext);
    const [formData, setFormData] = useState({});
    const [files, setFiles] = useState({ first_img: null, card_img: null });
    const [newImages, setNewImages] = useState([]); // For multiple gallery images upload
    const [newImagePreviews, setNewImagePreviews] = useState([]);
    const [existingImages, setExistingImages] = useState([]); // Current product images from backend
    const [deleteImageIds, setDeleteImageIds] = useState([]); // IDs of gallery images to delete
    

    const [previews, setPreviews] = useState({ first_img: null, card_img: null });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    useEffect(() => {
        if (product) {
            setFormData({
                title: product.title || "",
                category_id: product.category_id || product.category?.id || "",
                brand_id: product.brand_id || product.brand?.id || "",
                store_id: product.store_id || product.store?.id || "",
                description: decodeHtmlEntities(product.description),
                short_description: decodeHtmlEntities(product.short_description),
                product_condition: product.product_condition || "",
                selling_style: product.selling_style || "",
                buying_price: product.pricing?.buying_price || product.buying_price || "",
                short_buying_price: product.pricing?.short_buying_price || product.short_buying_price || "",
                selling_price_dollar: product.pricing?.selling_price_dollar || product.selling_price_dollar || "",
                sell_price_dollar: product.pricing?.sell_price_dollar || product.sell_price_dollar || "",
                auction_min_price: product.pricing?.auction_min_price ?? product.auction_min_price ?? "",
                shipping_charges_dollar: product.pricing?.shipping_charges_dollar || product.shipping_charges_dollar || "",
                qty: product.qty || ""
            });
            setFiles({ first_img: null, card_img: null });
            setPreviews({
                first_img: product.img?.first_img ? `${imgBaseUrl}${product.img.first_img}` : null,
                card_img: product.img?.card_img ? `${imgBaseUrl}${product.img.card_img}` : null
            });
            setExistingImages(product.images || []);
            setNewImages([]);
            setNewImagePreviews([]);
            setDeleteImageIds([]);
            setError(null);
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, description: content }));
    };

    const handleShortDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, short_description: content }));
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            const file = selectedFiles[0];
            setFiles((prev) => ({ ...prev, [name]: file }));
            setPreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
        }
    };

    // Handle multiple new gallery images
    const handleNewImagesChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length > 0) {
            setNewImages((prev) => [...prev, ...selectedFiles]);
            const previewsArr = selectedFiles.map((file) => URL.createObjectURL(file));
            setNewImagePreviews((prev) => [...prev, ...previewsArr]);
        }
    };

    // Remove newly selected gallery image before submission
    const removeNewImage = (index) => {
        setNewImages((prev) => prev.filter((_, i) => i !== index));
        setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
    };

    // Mark existing image for deletion
    const handleDeleteExistingImage = (imageId) => {
        setDeleteImageIds((prev) => [...prev, imageId]);
        setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const token = localStorage.getItem("authToken");
            const data = new FormData();

            // Append standard form fields
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== "" && formData[key] !== null && formData[key] !== undefined) {
                    data.append(key, formData[key]);
                }
            });

            // Append main images if updated
            if (files.first_img) data.append("first_img", files.first_img);
            if (files.card_img) data.append("card_img", files.card_img);

            // Append gallery image deletions (delete_image_ids[])
            deleteImageIds.forEach((id) => {
                data.append("delete_image_ids[]", id);
            });

            // Append new gallery images (images[])
            newImages.forEach((file) => {
                data.append("images[]", file);
            });

            data.append("_method", "PUT");

            // Fixed URL to include /admin/products/{id}
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/products/${product.id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                        "X-HTTP-Method-Override": "PUT"
                    }
                }
            );

            if (res.status === 200 || res.data?.success) {
                onUpdateSuccess(res.data?.data || res.data);
                handleClose();
            }
        } catch (err) {
            console.error("Error updating product:", err);
            setError(err.response?.data?.message || "Failed to update product.");
        } finally {
            setSubmitting(false);
        }
    };

    const editorToolbarOptions = [
        ["undo", "redo"],
        ["font", "fontSize", "formatBlock"],
        ["bold", "underline", "italic", "strike", "subscript", "superscript"],
        ["fontColor", "hiliteColor"],
        ["removeFormat"],
        ["outdent", "indent"],
        ["align", "horizontalRule", "list", "lineHeight"],
        ["table", "link", "image", "video"],
        ["fullScreen", "showBlocks", "codeView"]
    ];

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered className="custom-edit-modal">
            <Modal.Header closeButton className="px-4 py-3 border-bottom">
                <Modal.Title className="fw-bold fs-5 text-dark">{t("Edit Product")}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="px-4 py-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Title")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={formData.title || ""}
                                    onChange={handleChange}
                                    required
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Brand")}</Form.Label>
                                <Form.Select name="brand_id" value={formData.brand_id || ""} onChange={handleChange} className="custom-input">
                                    <option value="">{t("Select Brand")}</option>
                                    {filterData.brands?.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Store")}</Form.Label>
                                <Form.Select name="store_id" value={formData.store_id || ""} onChange={handleChange} className="custom-input">
                                    <option value="">{t("Select Store")}</option>
                                    {filterData.stores?.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Quantity")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="qty"
                                    value={formData.qty || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Buying Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="buying_price"
                                    value={formData.buying_price || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Selling Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="selling_price_dollar"
                                    value={formData.selling_price_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Sell Price ($)")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="sell_price_dollar"
                                    value={formData.sell_price_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        {formData.selling_style === "Auction" && (
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Auction Min Price ($)")}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="auction_min_price"
                                        value={formData.auction_min_price ?? ""}
                                        onChange={handleChange}
                                        className="custom-input"
                                    />
                                </Form.Group>
                            </Col>
                        )}

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Short Buying Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="short_buying_price"
                                    value={formData.short_buying_price || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Shipping Charges ($)")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="shipping_charges_dollar"
                                    value={formData.shipping_charges_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Product Condition")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="product_condition"
                                    value={formData.product_condition || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Selling Style")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="selling_style"
                                    value={formData.selling_style || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Short Description")}</Form.Label>
                                <div className="sun-editor-wrapper">
                                    <SunEditor
                                        setContents={formData.short_description || ""}
                                        onChange={handleShortDescriptionChange}
                                        setOptions={{
                                            buttonList: editorToolbarOptions,
                                            height: 150,
                                        }}
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Description")}</Form.Label>
                                <div className="sun-editor-wrapper">
                                    <SunEditor
                                        setContents={formData.description || ""}
                                        onChange={handleDescriptionChange}
                                        setOptions={{
                                            buttonList: editorToolbarOptions,
                                            height: 200,
                                        }}
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        {/* Main Images */}
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("First Image")}</Form.Label>
                                <Form.Control type="file" name="first_img" onChange={handleFileChange} accept="image/*" className="custom-input mb-2" />
                                {previews.first_img && (
                                    <div className="p-1 border rounded bg-light d-inline-block">
                                        <Image src={previews.first_img} alt="First Image Preview" rounded style={{ width: "80px", height: "80px", objectFit: "contain" }} />
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Card Image")}</Form.Label>
                                <Form.Control type="file" name="card_img" onChange={handleFileChange} accept="image/*" className="custom-input mb-2" />
                                {previews.card_img && (
                                    <div className="p-1 border rounded bg-light d-inline-block">
                                        <Image src={previews.card_img} alt="Card Image Preview" rounded style={{ width: "80px", height: "80px", objectFit: "contain" }} />
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        {/* Existing Gallery Images (with Delete Option) */}
                        {existingImages.length > 0 && (
                            <Col md={12}>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Existing Gallery Images")}</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {existingImages.map((imgObj) => (
                                        <div key={imgObj.id} className="position-relative border p-1 rounded bg-light">
                                            <Image
                                                src={`${imgBaseUrl}${imgObj.image}`}
                                                alt="Gallery Image"
                                                rounded
                                                style={{ width: "80px", height: "80px", objectFit: "contain" }}
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute top-0 end-0 p-0 px-1"
                                                style={{ fontSize: "10px" }}
                                                onClick={() => handleDeleteExistingImage(imgObj.id)}
                                            >
                                                &times;
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        )}

                        {/* Upload New Gallery Images */}
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Add New Gallery Images")}</Form.Label>
                                <Form.Control type="file" multiple onChange={handleNewImagesChange} accept="image/*" className="custom-input mb-2" />
                                {newImagePreviews.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2">
                                        {newImagePreviews.map((src, idx) => (
                                            <div key={idx} className="position-relative border p-1 rounded bg-light">
                                                <Image src={src} alt="New Preview" rounded style={{ width: "80px", height: "80px", objectFit: "contain" }} />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 p-0 px-1"
                                                    style={{ fontSize: "10px" }}
                                                    onClick={() => removeNewImage(idx)}
                                                >
                                                    &times;
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="px-4 py-3 border-top bg-light">
                    <Button variant="secondary" onClick={handleClose} disabled={submitting} className="px-4">
                        {t("CANCEL")}
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting} className="px-4">
                        {submitting ? <Spinner animation="border" size="sm" /> : t("SAVE CHANGES")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}